import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Badge } from '../../components/Badge';
import { Modal } from '../../components/Modal';
import { Check, Copy, KeyRound, Monitor, Plus, ShieldAlert, Sparkles, XCircle, Sliders, Building2 } from 'lucide-react';

export function LicensesPage() {
  const [licenses, setLicenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal generation state
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'TRIAL' | 'STARTER' | 'PRO' | 'ENTERPRISE' | 'CUSTOM'>('PRO');
  const [selectedDuration, setSelectedDuration] = useState('ONE_YEAR');
  const [maxDevices, setMaxDevices] = useState<number>(50);
  const [licenseNotes, setLicenseNotes] = useState('');
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedLicenseId, setCopiedLicenseId] = useState<string | null>(null);

  const fetchLicenses = async () => {
    try {
      const res = await api.superadmin.getLicenses();
      setLicenses(res.licenses);
    } catch (err) {
      console.error('Failed to fetch licenses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, []);

  const handleOpenGenerate = () => {
    setGeneratedKey(null);
    setCopied(false);
    setSelectedPlan('PRO');
    setSelectedDuration('ONE_YEAR');
    setMaxDevices(50);
    setLicenseNotes('');
    setGenerateModalOpen(true);
  };

  const handleSelectPlan = (plan: 'TRIAL' | 'STARTER' | 'PRO' | 'ENTERPRISE' | 'CUSTOM') => {
    setSelectedPlan(plan);
    if (plan === 'TRIAL') {
      setSelectedDuration('SEVEN_DAYS');
      setMaxDevices(5);
    } else if (plan === 'STARTER') {
      setSelectedDuration('ONE_YEAR');
      setMaxDevices(15);
    } else if (plan === 'PRO') {
      setSelectedDuration('ONE_YEAR');
      setMaxDevices(50);
    } else if (plan === 'ENTERPRISE') {
      setSelectedDuration('ONE_YEAR');
      setMaxDevices(9999);
    }
  };

  const handleGenerateLicense = async () => {
    setGenerating(true);
    try {
      const res = await api.superadmin.generateLicense({
        duration: selectedDuration,
        plan: selectedPlan,
        maxDevices: Number(maxDevices) || 50,
        notes: licenseNotes.trim() || undefined,
      });
      const raw = res.license.rawKey || res.license.plainKey;
      setGeneratedKey(raw);
      await fetchLicenses();
    } catch (err: any) {
      alert(err.message || 'Failed to generate license key');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyKey = () => {
    if (!generatedKey) return;
    navigator.clipboard.writeText(generatedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleCopyLicense = (keyToCopy: string, licId: string) => {
    navigator.clipboard.writeText(keyToCopy);
    setCopiedLicenseId(licId);
    setTimeout(() => setCopiedLicenseId(null), 2500);
  };

  const handleRevoke = async (id: string, keyMasked: string) => {
    if (!confirm(`Are you sure you want to revoke license ${keyMasked}? The associated organization will lose access immediately.`)) {
      return;
    }
    try {
      await api.superadmin.revokeLicense(id);
      await fetchLicenses();
    } catch (err: any) {
      alert(err.message || 'Failed to revoke license');
    }
  };

  const durationOptions = [
    { value: 'SEVEN_DAYS', label: '7 Days', desc: 'Proof of concept / evaluation trial' },
    { value: 'ONE_MONTH', label: '1 Month', desc: 'Standard monthly billing interval' },
    { value: 'ONE_YEAR', label: '1 Year', desc: 'Annual commercial enterprise contract' },
    { value: 'LIFETIME', label: 'Lifetime', desc: 'Permanent ecosystem partner license' },
  ];

  const planPresets = [
    {
      id: 'TRIAL',
      name: '7-Day Trial',
      seats: '5 PCs',
      badge: 'Free POC',
      color: 'border-zinc-700 hover:border-zinc-500',
      activeColor: 'border-emerald-500 bg-emerald-500/10 text-emerald-300',
    },
    {
      id: 'STARTER',
      name: 'Starter Tier',
      seats: '15 PCs',
      badge: '$3 / PC / mo',
      color: 'border-zinc-700 hover:border-zinc-500',
      activeColor: 'border-blue-500 bg-blue-500/10 text-blue-300',
    },
    {
      id: 'PRO',
      name: 'Pro Tier',
      seats: '50 PCs',
      badge: '$5 / PC / mo (Popular)',
      color: 'border-zinc-700 hover:border-zinc-500',
      activeColor: 'border-pulse-blue bg-pulse-blue/15 text-white',
    },
    {
      id: 'ENTERPRISE',
      name: 'Enterprise',
      seats: 'Unlimited PCs',
      badge: '$8 / PC / mo',
      color: 'border-zinc-700 hover:border-zinc-500',
      activeColor: 'border-amber-500 bg-amber-500/10 text-amber-300',
    },
    {
      id: 'CUSTOM',
      name: 'Custom Tier',
      seats: 'Custom Limit',
      badge: 'Manual Quota',
      color: 'border-zinc-700 hover:border-zinc-500',
      activeColor: 'border-purple-500 bg-purple-500/10 text-purple-300',
    },
  ];

  const getPlanBadge = (planName?: string) => {
    switch (planName?.toUpperCase()) {
      case 'TRIAL':
        return (
          <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
            TRIAL (5 PCs)
          </span>
        );
      case 'STARTER':
        return (
          <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-blue-500/15 text-blue-400 border border-blue-500/30">
            STARTER (15 PCs)
          </span>
        );
      case 'PRO':
        return (
          <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-pulse-blue/20 text-pulse-blue border border-pulse-blue/40 shadow-sm shadow-pulse-blue/10">
            PRO (50 PCs)
          </span>
        );
      case 'ENTERPRISE':
        return (
          <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-amber-500/15 text-amber-400 border border-amber-500/30">
            ENTERPRISE
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-purple-500/15 text-purple-400 border border-purple-500/30">
            {planName || 'COMMERCIAL'}
          </span>
        );
    }
  };

  return (
    <div className="p-8 space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-white tracking-tight">License & Quota Management</h1>
            <span className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider rounded bg-pulse-blue/10 text-pulse-blue border border-pulse-blue/30">
              CSPRNG Engine
            </span>
          </div>
          <p className="text-xs text-zinc-400 mt-1">
            Issue cryptographically secure activation keys with per-workstation seat limits & duration controls
          </p>
        </div>

        <button
          onClick={handleOpenGenerate}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-medium rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white shadow-lg shadow-pulse-blue/20 transition-all"
        >
          <Plus className="w-4 h-4" />
          <span>Generate New License</span>
        </button>
      </div>

      {/* Onboarding & Guide */}
      <div className="p-4 rounded-2xl bg-zinc-900/60 border border-[#222222] text-xs text-zinc-300 flex items-start gap-3.5">
        <div className="p-2 rounded-xl bg-pulse-blue/10 border border-pulse-blue/20 text-pulse-blue shrink-0">
          <KeyRound className="w-4 h-4" />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-white">Per-Workstation Licensing (Option 2)</span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium">
              Device Limit Enforced
            </span>
          </div>
          <p className="text-zinc-400 leading-relaxed text-[11px]">
            Every license key stores an exact workstation quota (e.g. 5, 15, 50, or Unlimited PCs).
            When customer organizations enroll workstations via the Windows Agent, the backend automatically enforces this limit.
            Admins activate keys at{' '}
            <a href="/activate" target="_blank" rel="noreferrer" className="text-pulse-blue underline hover:text-pulse-blueHover font-mono">
              /activate
            </a>.
          </p>
        </div>
      </div>

      {/* Licenses Table */}
      <div className="pulse-card rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#161616] text-zinc-400 uppercase tracking-wider font-medium border-b border-[#222222]">
              <tr>
                <th className="py-3 px-4">License Key</th>
                <th className="py-3 px-4">Plan</th>
                <th className="py-3 px-4">Device Quota</th>
                <th className="py-3 px-4">Duration</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Assigned Tenant</th>
                <th className="py-3 px-4">Created</th>
                <th className="py-3 px-4">Expires</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1D1D1D] text-zinc-300">
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-zinc-500">
                    Loading cryptographic licenses...
                  </td>
                </tr>
              ) : licenses.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-zinc-500">
                    No licenses generated yet.
                  </td>
                </tr>
              ) : (
                licenses.map((lic) => {
                  const keyVal = lic.rawKey || lic.keyMasked;
                  const isCopied = copiedLicenseId === lic.id;
                  const maxDev = lic.maxDevices !== undefined ? Number(lic.maxDevices) : null;

                  return (
                    <tr key={lic.id} className="hover:bg-zinc-900/40 transition-colors">
                      <td className="py-3.5 px-4">
                        <div className="inline-flex items-center gap-2 bg-zinc-950/80 px-2.5 py-1 rounded-lg border border-zinc-800/80">
                          <span className="font-mono font-medium text-white tracking-wider">
                            {keyVal}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopyLicense(keyVal, lic.id)}
                            title="Copy License Key"
                            className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
                          >
                            {isCopied ? (
                              <Check className="w-3.5 h-3.5 text-emerald-400" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                        {lic.notes && (
                          <div className="text-[10px] text-zinc-500 mt-0.5 italic">
                            Ref: {lic.notes}
                          </div>
                        )}
                      </td>
                      <td className="py-3.5 px-4">
                        {getPlanBadge(lic.plan)}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="inline-flex items-center gap-1.5 font-medium text-zinc-200">
                          <Monitor className="w-3.5 h-3.5 text-zinc-400" />
                          <span>
                            {maxDev && maxDev >= 9999
                              ? 'Unlimited PCs'
                              : maxDev
                              ? `${maxDev} PCs Limit`
                              : 'Standard'}
                          </span>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 font-medium text-zinc-200">
                        {lic.duration.replace('_', ' ')}
                      </td>
                      <td className="py-3.5 px-4">
                        <Badge status={lic.status} />
                      </td>
                      <td className="py-3.5 px-4">
                        {lic.organization ? (
                          <span className="font-medium text-white">{lic.organization.name}</span>
                        ) : (
                          <span className="text-zinc-500 italic">Unassigned</span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-zinc-400">
                        {new Date(lic.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3.5 px-4 text-zinc-400">
                        {lic.duration === 'LIFETIME'
                          ? 'Never'
                          : lic.expiresAt
                          ? new Date(lic.expiresAt).toLocaleDateString()
                          : 'Upon activation'}
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        {lic.status !== 'REVOKED' ? (
                          <button
                            onClick={() => handleRevoke(lic.id, lic.keyMasked)}
                            className="px-2.5 py-1 text-xs rounded-lg text-rose-400 hover:bg-rose-950/40 border border-transparent hover:border-rose-800/50 transition-colors"
                          >
                            Revoke
                          </button>
                        ) : (
                          <span className="text-[11px] text-zinc-600">Revoked</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Generate License Modal with Option 2 Per-Seat Controls */}
      <Modal
        isOpen={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        title="Generate License & Configure Quota"
        description="Select Option 2 plan tier and workstation limits. Plaintext activation key is displayed once upon creation."
        maxWidth="max-w-xl"
      >
        {!generatedKey ? (
          <div className="space-y-5">
            {/* Step 1: Select Plan Tier */}
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 block mb-2">
                1. Select Option 2 Plan Tier
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {planPresets.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => handleSelectPlan(preset.id as any)}
                    className={`p-2.5 rounded-xl text-left border transition-all ${
                      selectedPlan === preset.id
                        ? preset.activeColor
                        : `bg-zinc-900/60 ${preset.color} text-zinc-300`
                    }`}
                  >
                    <div className="font-semibold text-xs text-white flex items-center justify-between">
                      <span>{preset.name}</span>
                      {selectedPlan === preset.id && <Check className="w-3.5 h-3.5 text-pulse-blue" />}
                    </div>
                    <div className="text-[11px] font-mono text-zinc-300 mt-0.5">{preset.seats}</div>
                    <div className="text-[10px] text-zinc-400 mt-0.5">{preset.badge}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Quota & Duration Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-[#222222]">
              {/* Max Workstations input */}
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1.5 flex items-center gap-1.5">
                  <Monitor className="w-3.5 h-3.5 text-pulse-blue" />
                  <span>Workstation Limit (Max PCs)</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="9999"
                    value={maxDevices}
                    onChange={(e) => setMaxDevices(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white font-mono focus:outline-none focus:border-pulse-blue"
                    placeholder="e.g. 15, 50, or 9999"
                  />
                  <span className="absolute right-3 top-2.5 text-[10px] text-zinc-500 font-medium pointer-events-none">
                    {maxDevices >= 9999 ? 'Unlimited' : 'PCs'}
                  </span>
                </div>
                <p className="text-[10px] text-zinc-500 mt-1">
                  Enforces agent enrollment limit. Set 9999 for unlimited.
                </p>
              </div>

              {/* Customer Notes */}
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1.5 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-zinc-400" />
                  <span>Customer Reference / Note</span>
                </label>
                <input
                  type="text"
                  value={licenseNotes}
                  onChange={(e) => setLicenseNotes(e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-pulse-blue"
                  placeholder="e.g. Acme Corp 15-Seat Annual"
                />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Internal tag or customer name for audit tracking.
                </p>
              </div>
            </div>

            {/* Step 3: Duration Selection */}
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 block mb-2">
                2. Subscription Duration
              </label>
              <div className="grid grid-cols-2 gap-2">
                {durationOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSelectedDuration(opt.value)}
                    className={`p-2.5 rounded-xl text-left border transition-all ${
                      selectedDuration === opt.value
                        ? 'bg-pulse-blue/10 border-pulse-blue text-white shadow-sm'
                        : 'bg-zinc-900/60 border-zinc-800 text-zinc-300 hover:border-zinc-700'
                    }`}
                  >
                    <div className="font-medium text-xs text-white">{opt.label}</div>
                    <div className="text-[10px] text-zinc-400 mt-0.5">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#222222]">
              <button
                onClick={() => setGenerateModalOpen(false)}
                className="px-3 py-2 text-xs font-medium rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateLicense}
                disabled={generating}
                className="px-4 py-2 text-xs font-medium rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white shadow-md shadow-pulse-blue/20 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                <KeyRound className="w-3.5 h-3.5" />
                <span>{generating ? 'Issuing License...' : 'Issue Option 2 License'}</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            <div className="p-4 rounded-xl bg-emerald-950/30 border border-emerald-800/60 text-emerald-300 text-xs flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold block text-white">License Generated Successfully!</span>
                <span className="text-[11px] text-emerald-200">
                  Tier: <strong className="text-white">{selectedPlan}</strong> • Quota:{' '}
                  <strong className="text-white">{maxDevices >= 9999 ? 'Unlimited' : `${maxDevices} PCs`}</strong>
                </span>
                <p className="mt-1 text-zinc-400 text-[11px]">
                  Provide this activation key to the customer admin. The limit will be automatically assigned when they register.
                </p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800 flex items-center justify-between gap-4">
              <span className="font-mono text-base font-semibold text-white tracking-widest selection:bg-pulse-blue">
                {generatedKey}
              </span>
              <button
                onClick={handleCopyKey}
                className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? 'Copied!' : 'Copy Key'}</span>
              </button>
            </div>

            <div className="text-right pt-2 border-t border-[#222222]">
              <button
                onClick={() => setGenerateModalOpen(false)}
                className="px-4 py-2 text-xs font-medium rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
